<div class="intro-step">
  @switch (phase()) {
    @case ('scanning') {
      <!-- Scanning State -->
      <div class="intro-content">
        <h2 class="intro-title">{{ scanningTitle }}</h2>
        <p class="intro-description">
          Analyzing <strong>{{ processedCount }}</strong> bill{{ processedCount === 1 ? '' : 's' }}...
        </p>
      </div>
      <div class="scanning-state">
        <mat-spinner diameter="40"></mat-spinner>
        <p class="scanning-label">{{ scanningLabel }}</p>
        <p class="scanning-sub">{{ scanningSubtext }}</p>
      </div>
    }

    @case ('error') {
      <!-- Error State -->
      <div class="intro-content">
        <h2 class="intro-title">Let's review your portfolio</h2>
        <p class="intro-description">
          I found <strong>{{ processedCount }}</strong> bill{{ processedCount === 1 ? '' : 's' }} ready for review.
        </p>
      </div>
      <div class="error-state">
        <i class="material-symbols-rounded">error</i>
        <span>{{ state.error() }}</span>
        <button class="btn-retry" (click)="scanForMatches()">
          <i class="material-symbols-rounded">refresh</i>
          Retry
        </button>
      </div>

      <!-- Fallback: treat as new -->
      <div class="fallback-actions">
        <button class="action-card new-card" (click)="treatAsNew()">
          <div class="card-icon">
            <i class="material-symbols-rounded">add_circle</i>
          </div>
          <div class="card-body">
            <span class="card-title">Treat everything as new</span>
            <span class="card-description">Skip matching and create all entities fresh.</span>
          </div>
          <i class="material-symbols-rounded card-arrow">arrow_forward</i>
        </button>
      </div>
    }

    @case ('results') {
      <!-- Results State: Jen Zen presents findings + customer selection -->
      <div class="intro-content">
        <h2 class="intro-title">
          @if (state.hasGoodCustomerMatch()) {
            I found a potential match!
          } @else {
            Let's get started
          }
        </h2>
        <p class="intro-description">
          @if (state.hasGoodCustomerMatch() && state.selectedCustomerId() !== null) {
            I analyzed <strong>{{ processedCount }}</strong> bill{{ processedCount === 1 ? '' : 's' }}
            and found {{ fastTrackEntityCount() }} entit{{ fastTrackEntityCount() === 1 ? 'y' : 'ies' }} to import.
          } @else {
            I analyzed <strong>{{ processedCount }}</strong> bill{{ processedCount === 1 ? '' : 's' }}.
            @if (state.hasGoodCustomerMatch()) {
              Select a customer to link to, or create a new one.
            } @else if (state.customerOptions().length > 1) {
              I didn't find any strong matches for an existing customer. Create a new one or link to an existing customer below.
            } @else {
              A new customer will be created.
            }
          }
        </p>
      </div>

      <!-- Entity counts summary -->
      <div class="detail-counts">
        <span class="detail-count">
          <i class="material-symbols-rounded">account_balance</i>
          {{ newCustomerPreview.legalEntityNames.length }} entit{{ newCustomerPreview.legalEntityNames.length !== 1 ? 'ies' : 'y' }}
        </span>
        <span class="detail-count">
          <i class="material-symbols-rounded">home_work</i>
          {{ newCustomerPreview.serviceAddressCount }} address{{ newCustomerPreview.serviceAddressCount !== 1 ? 'es' : '' }}
        </span>
        <span class="detail-count">
          <i class="material-symbols-rounded">speed</i>
          {{ newCustomerPreview.meterCount }} meter{{ newCustomerPreview.meterCount !== 1 ? 's' : '' }}
        </span>
        <span class="detail-count">
          <i class="material-symbols-rounded">receipt_long</i>
          {{ newCustomerPreview.billCount }} bill{{ newCustomerPreview.billCount !== 1 ? 's' : '' }}
        </span>
      </div>

      <!-- Customer Cards -->
      <div class="customer-cards">
        @for (option of state.customerOptions(); track option.value) {
          <div class="customer-card-wrapper">
            <button
              class="customer-card"
              [class.selected]="state.selectedCustomerId() === option.value"
              [class.create-new]="option.isCreateNew"
              (click)="selectCustomer(option.value, option.isCreateNew ? newCustomerPreview.suggestedName : option.label)">
              <div class="card-radio">
                <mat-radio-button
                  [checked]="state.selectedCustomerId() === option.value"
                  [value]="option.value"
                  disableRipple
                ></mat-radio-button>
              </div>
              <div class="card-content">
                @if (option.isCreateNew) {
                  <div class="card-icon create-new-icon">
                    <i class="material-symbols-rounded">add_business</i>
                  </div>
                  <div class="card-detail">
                    <span class="card-name">Create New Customer</span>
                    <span class="card-sub">All entities will be created fresh</span>
                  </div>
                } @else {
                  <div class="card-icon match-icon">
                    <i class="material-symbols-rounded">language</i>
                  </div>
                  <div class="card-detail">
                    <span class="card-name">{{ option.label }}</span>
                    <span class="card-sub">
                      <span class="match-score">{{ (option.score! * 100) | number:'1.0-0' }}% match</span>
                    </span>
                  </div>
                }
              </div>
            </button>

            <!-- Detail panel for selected option -->
            @if (state.selectedCustomerId() === option.value) {
              <div class="card-detail-panel">
                @if (option.isCreateNew) {
                  <!-- Customer Details Section -->
                  <div class="details-section">
                    <div class="section-header">
                      <i class="material-symbols-rounded">language</i>
                      <span>Customer Details</span>
                    </div>

                    <!-- Customer Name -->
                    <div class="field-section">
                      <div class="field-label">Customer Name *</div>
                      <mat-radio-group [ngModel]="nameMode()" (ngModelChange)="setNameMode($event)" class="radio-field-group">
                        <!-- Existing Names Option -->
                        @if (availableCustomerNames.length > 0) {
                          <div class="radio-option" [class.selected]="nameMode() === 'existing'">
                            <mat-radio-button value="existing" class="platform-radio">
                              <span class="radio-label">Extracted Names</span>
                            </mat-radio-button>
                            @if (nameMode() === 'existing') {
                              <mat-form-field appearance="outline" class="inline-mat-select">
                                <mat-select [ngModel]="customerDetails().customerName"
                                            (ngModelChange)="selectCustomerName($event)">
                                  @for (name of availableCustomerNames; track name) {
                                    <mat-option [value]="name">{{ name }}</mat-option>
                                  }
                                </mat-select>
                                <i class="material-symbols-rounded" matSuffix>expand_more</i>
                              </mat-form-field>
                            }
                          </div>
                        }
                        <!-- Custom Name Option -->
                        <div class="radio-option" [class.selected]="nameMode() === 'custom' || availableCustomerNames.length === 0">
                          <mat-radio-button value="custom" class="platform-radio">
                            <span class="radio-label">Enter Custom Name</span>
                          </mat-radio-button>
                          @if (nameMode() === 'custom' || availableCustomerNames.length === 0) {
                            <input type="text"
                                   class="inline-input"
                                   #customerName="ngModel"
                                   [ngModel]="customerDetails().customerName"
                                   (ngModelChange)="onDetailsFieldChange('customerName', $event)"
                                   required
                                   placeholder="Enter customer name" />
                          }
                        </div>
                      </mat-radio-group>
                    </div>

                    <!-- Billing Address -->
                    <div class="field-section">
                      <div class="field-label">Billing Address</div>
                      <mat-radio-group [ngModel]="addressMode()" (ngModelChange)="setAddressMode($event)" class="radio-field-group">
                        <!-- Existing Addresses Option -->
                        @if (availableBillingAddresses.length > 0) {
                          <div class="radio-option" [class.selected]="addressMode() === 'existing'">
                            <mat-radio-button value="existing" class="platform-radio">
                              <span class="radio-label">Extracted Addresses</span>
                            </mat-radio-button>
                            @if (addressMode() === 'existing') {
                              <mat-form-field appearance="outline" class="inline-mat-select">
                                <mat-select [ngModel]="selectedAddressLabel()"
                                            (ngModelChange)="selectBillingAddressByLabel($event)">
                                  @for (addr of availableBillingAddresses; track addr.label) {
                                    <mat-option [value]="addr.label">{{ addr.label }}</mat-option>
                                  }
                                </mat-select>
                                <i class="material-symbols-rounded" matSuffix>expand_more</i>
                              </mat-form-field>
                            }
                          </div>
                        }
                        <!-- Custom Address Option -->
                        <div class="radio-option column" [class.selected]="addressMode() === 'custom' || availableBillingAddresses.length === 0">
                          <div class="radio-row">
                            <mat-radio-button value="custom" class="platform-radio">
                              <span class="radio-label">Enter Custom Address</span>
                            </mat-radio-button>
                          </div>
                          @if (addressMode() === 'custom' || availableBillingAddresses.length === 0) {
                            <div class="custom-address-fields">
                              <mat-form-field appearance="outline">
                                <mat-label>Street Address</mat-label>
                                <input matInput
                                       [ngModel]="customerDetails().address1"
                                       (ngModelChange)="onDetailsFieldChange('address1', $event)"
                                       placeholder="Street address" />
                              </mat-form-field>
                              <mat-form-field appearance="outline">
                                <mat-label>Address Line 2</mat-label>
                                <input matInput
                                       [ngModel]="customerDetails().address2"
                                       (ngModelChange)="onDetailsFieldChange('address2', $event)"
                                       placeholder="Suite, unit, etc." />
                              </mat-form-field>
                              <div class="address-row">
                                <mat-form-field appearance="outline" class="city-field">
                                  <mat-label>City</mat-label>
                                  <input matInput
                                         [ngModel]="customerDetails().city"
                                         (ngModelChange)="onDetailsFieldChange('city', $event)"
                                         placeholder="City" />
                                </mat-form-field>
                                <mat-form-field appearance="outline" class="state-field">
                                  <mat-label>State</mat-label>
                                  <input matInput
                                         [ngModel]="customerDetails().state"
                                         (ngModelChange)="onDetailsFieldChange('state', $event)"
                                         placeholder="ST"
                                         maxlength="2" />
                                </mat-form-field>
                                <mat-form-field appearance="outline" class="zip-field">
                                  <mat-label>ZIP</mat-label>
                                  <input matInput
                                         [ngModel]="customerDetails().zip"
                                         (ngModelChange)="onDetailsFieldChange('zip', $event)"
                                         placeholder="ZIP code" />
                                </mat-form-field>
                              </div>
                            </div>
                          }
                        </div>
                      </mat-radio-group>
                    </div>
                  </div>

                  <!-- Primary Contact Fields -->
                  <div class="contact-section">
                    <div class="contact-header">
                      <i class="material-symbols-rounded">person</i>
                      <span>Primary Contact</span>
                      <span class="required-note">* Required</span>
                    </div>
                    <div class="contact-fields">
                      <!-- Email comes first: it identifies an existing user so the
                           remaining fields auto-populate instead of being typed -->
                      <mat-form-field appearance="outline" class="contact-field full email">
                        <mat-label>Email *</mat-label>
                        <input matInput type="email"
                               #email="ngModel"
                               [ngModel]="contact().email"
                               (ngModelChange)="onContactFieldChange('email', $event)"
                               required
                               email
                               placeholder="email@company.com" />
                        @if (emailLookupStatus() === 'checking') {
                          <mat-spinner matSuffix diameter="16"></mat-spinner>
                        }
                        @if (email.invalid && email.touched) {
                          @if (email.errors?.['required']) {
                            <mat-error>Email is required</mat-error>
                          } @else if (email.errors?.['email']) {
                            <mat-error>Please enter a valid email</mat-error>
                          }
                        }
                      </mat-form-field>
                      @if (emailLookupStatus() === 'unavailable') {
                        <div class="contact-email-note error">
                          <i class="material-symbols-rounded">error</i>
                          <span>{{ emailLookupError() }}</span>
                        </div>
                      } @else if (emailLookupStatus() === 'linked') {
                        <div class="contact-email-note linked">
                          <i class="material-symbols-rounded">how_to_reg</i>
                          <span>Existing contact found — their details were filled in automatically.</span>
                        </div>
                      }
                      <mat-form-field appearance="outline" class="contact-field half">
                        <mat-label>First Name *</mat-label>
                        <input matInput
                               #firstName="ngModel"
                               [ngModel]="contact().firstName"
                               (ngModelChange)="onContactFieldChange('firstName', $event)"
                               required
                               [disabled]="firstNameLocked()"
                               placeholder="First name" />
                        @if (firstName.invalid && firstName.touched) {
                          <mat-error>First name is required</mat-error>
                        }
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="contact-field half">
                        <mat-label>Last Name *</mat-label>
                        <input matInput
                               #lastName="ngModel"
                               [ngModel]="contact().lastName"
                               (ngModelChange)="onContactFieldChange('lastName', $event)"
                               required
                               [disabled]="lastNameLocked()"
                               placeholder="Last name" />
                        @if (lastName.invalid && lastName.touched) {
                          <mat-error>Last name is required</mat-error>
                        }
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="contact-field half">
                        <mat-label>Phone</mat-label>
                        <input matInput type="tel"
                               [ngModel]="contact().phone"
                               (ngModelChange)="onContactFieldChange('phone', $event)"
                               [imask]="phoneMask"
                               [unmask]="phoneUnmask"
                               [disabled]="phoneLocked()"
                               placeholder="(555) 555-5555" />
                      </mat-form-field>
                    </div>
                  </div>
                } @else {
                  @if (option.matchReasons && option.matchReasons.length > 0) {
                    <div class="match-reasons">
                      <span class="reasons-label">Matched because:</span>
                      @for (reason of option.matchReasons; track reason) {
                        <span class="reason-chip">
                          <i class="material-symbols-rounded">check_circle</i>
                          {{ reason }}
                        </span>
                      }
                    </div>
                  }
                }
              </div>
            }
          </div>
        }
      </div>

      <!-- Error display -->
      @if (state.error()) {
        <div class="inline-error">
          <i class="material-symbols-rounded">error</i>
          {{ state.error() }}
        </div>
      }

      <!-- Actions -->
      <div class="actions-bar">
        <div></div>
        <button
          class="btn-primary"
          (click)="confirmCustomer()"
          [disabled]="!canContinueResult() || isCreatingCustomer">
          @if (isCreatingCustomer) {
            <mat-spinner diameter="18"></mat-spinner>
            Creating...
          } @else {
            Continue
            <i class="material-symbols-rounded">arrow_forward</i>
          }
        </button>
      </div>
    }

    @case ('choice') {
      <!-- Fast Track / Review Everything Choice -->
      <div class="intro-content">
        <h2 class="intro-title">How would you like to proceed?</h2>
        <p class="intro-description">
          I analyzed <strong>{{ processedCount }}</strong> bill{{ processedCount === 1 ? '' : 's' }}
          and found {{ fastTrackEntityCount() }} entities to import.
        </p>
      </div>

      <div class="fast-track-choice">
        <button
          class="choice-card fast-track-card"
          [disabled]="!fastTrackAnalysis()?.isEligible"
          (click)="chooseFastTrack()">
          <div class="choice-icon">
            <i class="material-symbols-rounded">bolt</i>
          </div>
          <div class="choice-body">
            <span class="choice-title">Express Import</span>
            <span class="choice-description">
              @if (fastTrackAnalysis()?.canFullyAutomate) {
                Auto-match everything and go straight to import.
              } @else {
                Auto-match {{ fastTrackEntityCount() }} entities, skip {{ fastTrackSkipCount() }} step{{ fastTrackSkipCount() !== 1 ? 's' : '' }}.
              }
            </span>
          </div>
          <i class="material-symbols-rounded choice-arrow">arrow_forward</i>
        </button>

        <button
          class="choice-card review-card"
          (click)="chooseReviewEverything()">
          <div class="choice-icon">
            <i class="material-symbols-rounded">manage_search</i>
          </div>
          <div class="choice-body">
            <span class="choice-title">Review Everything</span>
            <span class="choice-description">Walk through each step to review all matches and bill details.</span>
          </div>
          <i class="material-symbols-rounded choice-arrow">arrow_forward</i>
        </button>
      </div>

      @if (!fastTrackAnalysis()?.isEligible) {
        <div class="fast-track-disabled-note">
          <i class="material-symbols-rounded">info</i>
          <span>Fast track is not available: some entities need manual review.</span>
        </div>
      }
    }

    @case ('fast-tracking') {
      <!-- Express Import Progress Animation -->
      <div class="intro-content">
        <h2 class="intro-title">Setting up your express import</h2>
        <p class="intro-description">{{ fastTrackProgressMessage() }}</p>
      </div>
      <div class="scanning-state">
        <mat-spinner diameter="40"></mat-spinner>
        <p class="scanning-label">Working my magic...</p>
        <p class="scanning-sub">Matching entities and resolving conflicts</p>
      </div>
    }
  }
</div>
